import { 
    ApiResponse, 
    LogsResponse, 
    KytsResponse, 
    KytDetailResponse, 
    ChainSummaryResponse,
    LogsFilter,
    KytsFilter
} from '@/types/kyt-chain';

const API_BASE_URL = process.env.VUE_APP_BASE_URL || 'http://localhost:3000';

class KytChainService {
    private baseUrl = `${API_BASE_URL}api/kyt-chain`;
    private mainLogsBaseUrl = `${API_BASE_URL}api/main-logs`;

    // Helper method to build query string
    private buildQueryString(params: Record<string, any>): string {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                query.append(key, value.toString());
            }
        });
        return query.toString();
    }

    // Helper method to make API calls
    private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
                ...options.headers,
            };

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    }

    // Helper method for main logs API
    private async makeMainLogsRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
                ...options.headers,
            };

            const response = await fetch(`${this.mainLogsBaseUrl}${endpoint}`, {
                ...options,
                headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Main Logs API Error:', error);
            throw error;
        }
    }

    // Get Main KYTs
    async getMainKyts(filters: any = {}): Promise<any> {
        const queryString = this.buildQueryString(filters);
        const endpoint = `/main-kyts${queryString ? `?${queryString}` : ''}`;
        return this.makeMainLogsRequest(endpoint);
    }

    // Get Child KYTs with Log Number (for the new API)
    async getChildKytsWithLogNo(parentMainLogNo: string): Promise<any> {
        const endpoint = `/parent/${encodeURIComponent(parentMainLogNo)}/kyts-with-logno`;
        return this.makeMainLogsRequest(endpoint);
    }

    // Level 1: Get All KYT Logs
    async getAllLogs(filters: LogsFilter = {}): Promise<ApiResponse<LogsResponse>> {
        const queryString = this.buildQueryString(filters);
        const endpoint = `/logs${queryString ? `?${queryString}` : ''}`;
        return this.makeRequest<LogsResponse>(endpoint);
    }

    // Level 2: Get KYTs by Log Number
    async getKytsByLogNumber(logNo: string, filters: KytsFilter = {}): Promise<ApiResponse<KytsResponse>> {
        const queryString = this.buildQueryString(filters);
        const endpoint = `/logs/${encodeURIComponent(logNo)}/kyts${queryString ? `?${queryString}` : ''}`;
        return this.makeRequest<KytsResponse>(endpoint);
    }

    // Level 3: Get KYT Detail in Chain
    async getKytDetail(logNo: string, kytId: string): Promise<ApiResponse<KytDetailResponse>> {
        const endpoint = `/logs/${encodeURIComponent(logNo)}/kyts/${encodeURIComponent(kytId)}`;
        return this.makeRequest<KytDetailResponse>(endpoint);
    }

    // Summary: Get Chain Statistics
    async getChainSummary(): Promise<ApiResponse<ChainSummaryResponse>> {
        return this.makeRequest<ChainSummaryResponse>('/summary');
    }

    // Additional utility methods for specific operations

    // Refresh log data
    async refreshLogData(logNo: string): Promise<ApiResponse<any>> {
        return this.makeRequest<any>(`/logs/${encodeURIComponent(logNo)}/refresh`, {
            method: 'POST',
        });
    }

    // Export log data
    async exportLogData(logNo: string, format: 'csv' | 'excel' = 'excel'): Promise<Blob> {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${this.baseUrl}/logs/${encodeURIComponent(logNo)}/export?format=${format}`, {
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
            });

            if (!response.ok) {
                throw new Error(`Export failed: ${response.status}`);
            }

            return await response.blob();
        } catch (error) {
            console.error('Export Error:', error);
            throw error;
        }
    }

    // Get log statistics by date range
    async getLogStatsByDateRange(startDate: string, endDate: string): Promise<ApiResponse<any>> {
        const queryString = this.buildQueryString({ startDate, endDate });
        return this.makeRequest<any>(`/stats/date-range?${queryString}`);
    }

    // Search logs with advanced filters
    async searchLogs(searchQuery: string, filters: LogsFilter = {}): Promise<ApiResponse<LogsResponse>> {
        const searchFilters = { ...filters, search: searchQuery };
        return this.getAllLogs(searchFilters);
    }

    // Get employee performance in logs
    async getEmployeePerformance(employeeId: string, dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
        const params: any = { employeeId };
        if (dateRange) {
            params.startDate = dateRange.start;
            params.endDate = dateRange.end;
        }
        const queryString = this.buildQueryString(params);
        return this.makeRequest<any>(`/employee-performance?${queryString}`);
    }

    // Get site performance in logs
    async getSitePerformance(siteId: string, dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
        const params: any = { siteId };
        if (dateRange) {
            params.startDate = dateRange.start;
            params.endDate = dateRange.end;
        }
        const queryString = this.buildQueryString(params);
        return this.makeRequest<any>(`/site-performance?${queryString}`);
    }

    // Get completion trends
    async getCompletionTrends(period: 'week' | 'month' | 'quarter' = 'month'): Promise<ApiResponse<any>> {
        return this.makeRequest<any>(`/trends/completion?period=${period}`);
    }

    // Update log status (if update functionality is needed)
    async updateLogStatus(logNo: string, status: 'open' | 'partial' | 'closed', reason?: string): Promise<ApiResponse<any>> {
        return this.makeRequest<any>(`/logs/${encodeURIComponent(logNo)}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status, reason }),
        });
    }

    // Add comment to log
    async addLogComment(logNo: string, comment: string): Promise<ApiResponse<any>> {
        return this.makeRequest<any>(`/logs/${encodeURIComponent(logNo)}/comments`, {
            method: 'POST',
            body: JSON.stringify({ comment }),
        });
    }

    // Get log comments
    async getLogComments(logNo: string): Promise<ApiResponse<any>> {
        return this.makeRequest<any>(`/logs/${encodeURIComponent(logNo)}/comments`);
    }
}

// Create and export singleton instance
export const kytChainService = new KytChainService();
export default kytChainService;
